import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabaseHarvest, supabasePrestoV2 } from '@/lib/supabase';
import type { PbpCoverage, PbpPlay, PbpRollup } from '@/types/pbp';

export const GAME_PAGE_SIZE = 50;
export const PLAY_PAGE_SIZE = 100;
export interface CoverageFilters {
  year: number;
  gender: string;
  organization: string;
  outcome: string;
  grade: string;
  search: string;
  otherStatuses: boolean;
  page: number;
}

export function usePbpRollup(year: number, gender: string) {
  return useQuery({
    queryKey: ['pbp', 'rollup', year, gender],
    refetchInterval: 60_000,
    queryFn: async ({ signal }) => {
      // Snapshot only: the canonical live rollup exceeds PostgREST's timeout.
      // Each sport selects its latest atomically published checkpoint independently.
      const latest = await supabaseHarvest.from('presto_pbp_coverage_rollup_snapshot').select('written_at')
        .eq('sport_key', 'basketball').order('written_at', { ascending: false }).limit(1).abortSignal(signal);
      if (latest.error) throw latest.error;
      const writtenAt = latest.data?.[0]?.written_at as string | undefined;
      if (!writtenAt) return { rows: [] as PbpRollup[], writtenAt: null };
      let query = supabaseHarvest.from('presto_pbp_coverage_rollup_snapshot').select('*', { count: 'exact' })
        .eq('written_at', writtenAt)
        .eq('sport_key', 'basketball').eq('season_start_year', year)
        .order('organization_id').order('season_id').limit(1000).abortSignal(signal);
      if (gender === 'both') query = query.in('gender', ['M', 'W']);
      else if (gender) query = query.eq('gender', gender);
      const { data, error, count } = await query;
      if (error) throw error;
      if (count == null || count > (data?.length ?? 0)) throw new Error('Coverage totals are incomplete. Narrow the season or gender selection.');
      return { rows: (data ?? []) as PbpRollup[], writtenAt };
    },
  });
}

export function usePbpCoverage(filters: CoverageFilters, enabled = true) {
  return useQuery({
    queryKey: ['pbp', 'coverage', filters],
    enabled,
    queryFn: async ({ signal }) => {
      let query = supabaseHarvest.from('v_presto_pbp_coverage').select('*')
        .eq('sport_key', 'basketball').eq('season_start_year', filters.year)
        .order('event_id').range(filters.page * GAME_PAGE_SIZE, (filters.page + 1) * GAME_PAGE_SIZE)
        .abortSignal(signal);
      if (!filters.otherStatuses) query = query.eq('in_universe', true);
      if (filters.gender === 'both') query = query.in('gender', ['M', 'W']);
      else if (filters.gender) query = query.eq('gender', filters.gender);
      if (filters.organization) query = query.eq('organization_id', filters.organization);
      if (filters.outcome) query = query.eq('designation', filters.outcome);
      if (filters.search) query = query.eq('event_external_id', filters.search.trim());
      if (filters.grade) {
        query = query.contains('absence_evidence', { routes_checked: filters.grade === 'AB' ? ['A', 'B'] : ['A'] });
        if (filters.grade === 'A') query = query.not('absence_evidence', 'cs', JSON.stringify({ routes_checked: ['B'] }));
      }
      const { data, error } = await query;
      if (error) throw error;
      const rows = (data ?? []) as PbpCoverage[];
      return { rows: rows.slice(0, GAME_PAGE_SIZE), hasNext: rows.length > GAME_PAGE_SIZE };
    },
  });
}

export async function readPbpGame(eventExternalId: string, signal?: AbortSignal) {
  let query = supabaseHarvest.from('v_presto_pbp_coverage').select('*')
    .eq('sport_key', 'basketball').eq('event_external_id', eventExternalId);
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data as PbpCoverage | null;
}

export function usePbpGame(eventExternalId: string) {
  return useQuery({ queryKey: ['pbp', 'game', eventExternalId], queryFn: ({ signal }) => readPbpGame(eventExternalId, signal) });
}

export interface PlayFilters { period: string; side: string; player: string; narrative: string }
export class PbpRevisionChanged extends Error {
  constructor() { super('New source available · Reload feed'); this.name = 'PbpRevisionChanged'; }
}

/** Every page and the count use the stored-play revision, never the attempt ledger. */
export function usePbpPlays(game: PbpCoverage, pin: number | null, filters: PlayFilters) {
  return useInfiniteQuery({
    queryKey: ['pbp', 'plays', game.event_id, pin, filters],
    enabled: pin != null,
    initialPageParam: 0,
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const before = await readPbpGame(game.event_external_id, signal);
      if (before?.plays_manifest_id !== pin) throw new PbpRevisionChanged();
      let query = supabasePrestoV2.from('event_pbp_plays').select('*')
        .eq('event_id', game.event_id).eq('source_manifest_id', pin)
        .gt('play_seq', pageParam).order('play_seq').limit(PLAY_PAGE_SIZE + 1).abortSignal(signal);
      if (filters.period) query = query.eq('period_number', filters.period);
      if (filters.side) query = query.eq('vh', filters.side);
      if (filters.player) query = query.eq('checkname', filters.player);
      if (filters.narrative) query = query.ilike('action', `%${filters.narrative.replace(/[\\%_]/g, '\\$&')}%`);
      const [plays, count] = await Promise.all([
        query,
        supabasePrestoV2.from('event_pbp_plays').select('id', { count: 'exact', head: true })
          .eq('event_id', game.event_id).eq('source_manifest_id', pin).abortSignal(signal),
      ]);
      if (plays.error) throw plays.error;
      if (count.error) throw count.error;
      // Atomic replacement between requests must never look like a filtered-empty page.
      const after = await readPbpGame(game.event_external_id, signal);
      if (after?.plays_manifest_id !== pin || count.count === 0) throw new PbpRevisionChanged();
      const rows = (plays.data ?? []) as PbpPlay[];
      return { rows: rows.slice(0, PLAY_PAGE_SIZE), total: count.count,
        next: rows.length > PLAY_PAGE_SIZE ? rows[PLAY_PAGE_SIZE - 1].play_seq : undefined };
    },
    getNextPageParam: (last) => last.next,
  });
}
